import { useState } from 'react';
import Radios from './Radios';
import { Changed, changedRing } from './Changed';
import { fmtNum, inputCls } from '../../lib/q2c';
import { countWord } from '../../lib/countWord.mjs';

const OVERAGES = ['0', '3', '5', '10'];
const n = (v) => (v === '' || v == null ? NaN : Number(String(v).replace(/[,\s]/g, '')));

/**
 * 1. Scope (LP-402): floor area and the coverage of each item set every quantity on the quote.
 * Area in sf (m2 shown), the dry shake rate in lb/sf (kg/m2 shown), Trowel Hard in sf per gallon (m2 per L
 * shown), overage. The conversions and quantities come from the server (units.js at the scope's own rates).
 *
 * scope: the form's scope; sv: the builder view's scope (GET/POST /api/quotes/:id/builder);
 * set(path, value); tagOf(path): the Copilot change that tagged the field, if any.
 * note: why the scope sets no quantities now (the view's scope_note: two per-sf lines, none), or that
 * it did not load. Then it is said here and the scope can't be edited (an edit would set nothing).
 */
export default function ScopePanel({ scope, sv, editable: canEdit, set, tagOf, note = null }) {
  const [custom, setCustom] = useState({ shake: false, th: false, ov: false });
  if (!scope && !note) return null;
  const editable = canEdit && !note;
  const head = (
    <div>
      <h2 className="text-sm font-semibold">1. Scope</h2>
      <p className="text-xs text-[var(--text-secondary)]">Area and coverage set every quantity below. Change them here or tell Copilot.</p>
      {note && <p className="mt-2 rounded bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">{note}</p>}
    </div>
  );
  if (!scope) return <section className="space-y-3 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">{head}</section>;
  const std = sv?.standard || { dosage_lb_sf: 1.6, dosage_kg_m2: null, th_sf_per_gal: 205, th_m2_per_l: null };
  const shakeOurs = !custom.shake && n(scope.dosage_lb_sf) === std.dosage_lb_sf;
  const thOurs = !custom.th && n(scope.th_sf_per_gal) === std.th_sf_per_gal;
  const ov = String(n(scope.overage_pct) || 0);
  const ovChoice = !custom.ov && OVERAGES.includes(ov) ? ov : 'other';
  const ovTxt = n(scope.overage_pct) ? `, incl. ${fmtNum(n(scope.overage_pct), 0, 2)}% overage` : '';
  const t = (k) => tagOf(`scope.${k}`);

  return (
    <section className="space-y-3 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
      {head}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <label htmlFor="qb-area" className="text-xs font-medium uppercase tracking-wide text-[var(--text-secondary)]">Floor area</label>
        <span className="flex items-center gap-1">
          <input id="qb-area" className={`${inputCls} w-36 text-right tabular-nums ${changedRing(t('area_sf'))}`} inputMode="decimal" disabled={!editable}
            value={scope.area_sf} onChange={(e) => set('scope.area_sf', e.target.value)} />
          <span className="text-xs text-[var(--text-secondary)]">sf</span>
        </span>
        <span className="text-xs tabular-nums text-[var(--text-secondary)]">{sv ? `= ${fmtNum(sv.area_m2)} m²` : ''}</span>
        <Changed id={t('area_sf')} key={`a${t('area_sf') || ''}`} />
      </div>

      <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
        <table className="w-full min-w-[44rem] text-sm">
          <thead className="bg-gray-50 text-left text-xs dark:bg-slate-800">
            <tr><th className="p-2">Item</th><th className="p-2">Our standard</th><th className="p-2">This quote</th><th className="p-2">Quantity this sets</th></tr>
          </thead>
          <tbody className="align-top">
            {sv && (
              <tr className="border-t border-[var(--border)]">
                <td className="p-2"><p className="font-medium">{sv?.shake?.name || 'Dry shake'}</p>{sv?.shake?.sku && <p className="text-xs text-[var(--text-secondary)]">{sv.shake.sku}</p>}</td>
                <td className="p-2 tabular-nums"><p className="font-medium">{fmtNum(std.dosage_lb_sf, 0, 2)} lb/sf</p><p className="text-xs text-[var(--text-secondary)]">{std.dosage_kg_m2 != null ? `${fmtNum(std.dosage_kg_m2, 2, 2)} kg/m²` : ''}</p></td>
                <td className="p-2">
                  <Radios name="qb-cov-shake" disabled={!editable} value={shakeOurs ? 'ours' : 'other'}
                    options={[{ value: 'ours', label: `Use ${fmtNum(std.dosage_lb_sf, 0, 2)} lb/sf` }, { value: 'other', label: 'Use a different rate' }]}
                    onChange={(v) => { setCustom((c) => ({ ...c, shake: v === 'other' })); if (v === 'ours') set('scope.dosage_lb_sf', std.dosage_lb_sf); }} />
                  {!shakeOurs && (
                    <div className="ml-6 mt-1 flex flex-wrap items-center gap-1">
                      <input className={`${inputCls} w-24 text-right tabular-nums ${changedRing(t('dosage_lb_sf'))}`} inputMode="decimal" aria-label="Dry shake rate, lb/sf" disabled={!editable}
                        value={scope.dosage_lb_sf} onChange={(e) => set('scope.dosage_lb_sf', e.target.value)} />
                      <span className="text-xs text-[var(--text-secondary)]">lb/sf{sv?.dosage_kg_m2 != null ? ` = ${fmtNum(sv.dosage_kg_m2, 2, 2)} kg/m²` : ''}</span>
                    </div>
                  )}
                  <Changed id={t('dosage_lb_sf')} key={`d${t('dosage_lb_sf') || ''}`} />
                </td>
                <td className="p-2 tabular-nums">{sv?.shake ? (<><p className="font-medium">{fmtNum(sv.shake.kg)} kg</p><p className="text-xs text-[var(--text-secondary)]">{fmtNum(sv.shake.bags)} {countWord(sv.shake.bags, 'bag')} of {fmtNum(sv.shake.bag_kg)} kg, {fmtNum(sv.shake.pallets)} {countWord(sv.shake.pallets, 'pallet')}{ovTxt}</p></>) : '—'}</td>
              </tr>
            )}
            {sv?.th && (
              <tr className="border-t border-[var(--border)]">
                <td className="p-2"><p className="font-medium">{sv.th.name}</p>{sv.th.sku && <p className="text-xs text-[var(--text-secondary)]">{sv.th.sku}</p>}</td>
                <td className="p-2 tabular-nums"><p className="font-medium">{fmtNum(std.th_sf_per_gal)} sf per gal</p><p className="text-xs text-[var(--text-secondary)]">{std.th_m2_per_l != null ? `${fmtNum(std.th_m2_per_l, 2, 2)} m² per L` : ''}</p></td>
                <td className="p-2">
                  <Radios name="qb-cov-th" disabled={!editable} value={thOurs ? 'ours' : 'other'}
                    options={[{ value: 'ours', label: `Use ${fmtNum(std.th_sf_per_gal)} sf per gal` }, { value: 'other', label: 'Use a different rate' }]}
                    onChange={(v) => { setCustom((c) => ({ ...c, th: v === 'other' })); if (v === 'ours') set('scope.th_sf_per_gal', std.th_sf_per_gal); }} />
                  {!thOurs && (
                    <div className="ml-6 mt-1 flex flex-wrap items-center gap-1">
                      <input className={`${inputCls} w-24 text-right tabular-nums ${changedRing(t('th_sf_per_gal'))}`} inputMode="decimal" aria-label="Trowel Hard rate, sf per gallon" disabled={!editable}
                        value={scope.th_sf_per_gal} onChange={(e) => set('scope.th_sf_per_gal', e.target.value)} />
                      <span className="text-xs text-[var(--text-secondary)]">sf per gal{sv?.th_m2_per_l != null ? ` = ${fmtNum(sv.th_m2_per_l, 2, 2)} m² per L` : ''}</span>
                    </div>
                  )}
                  <Changed id={t('th_sf_per_gal')} key={`t${t('th_sf_per_gal') || ''}`} />
                </td>
                <td className="p-2 tabular-nums"><p className="font-medium">{fmtNum(sv.th.liters)} L</p><p className="text-xs text-[var(--text-secondary)]">{fmtNum(sv.th.canisters)} x {fmtNum(sv.th.canister_l)} L {countWord(sv.th.canisters, sv.th.unit)}{ovTxt}</p>{sv.th.by_rate !== sv.th.canisters && <p className="text-xs text-amber-700 dark:text-amber-300">As entered on the quote; the rate gives {fmtNum(sv.th.by_rate)}. Change the scope to recount.</p>}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="space-y-1.5">
        <p className="flex items-center gap-2 text-sm font-medium">Overage on material <Changed id={t('overage_pct')} key={`o${t('overage_pct') || ''}`} /></p>
        <Radios name="qb-ov" disabled={!editable} value={ovChoice}
          options={[{ value: '0', label: 'None, net quantities' }, { value: '3', label: '3%' }, { value: '5', label: '5%' }, { value: '10', label: '10%' }, { value: 'other', label: 'Other' }]}
          onChange={(v) => { setCustom((c) => ({ ...c, ov: v === 'other' })); if (v !== 'other') set('scope.overage_pct', Number(v)); }} />
        {ovChoice === 'other' && (
          <div className="ml-6 flex items-center gap-1">
            <input className={`${inputCls} w-20 text-right tabular-nums ${changedRing(t('overage_pct'))}`} inputMode="decimal" aria-label="Overage percent" disabled={!editable}
              value={scope.overage_pct} onChange={(e) => set('scope.overage_pct', e.target.value)} />
            <span className="text-xs text-[var(--text-secondary)]">%</span>
          </div>
        )}
        <p className="text-xs text-[var(--text-secondary)]">The dry shake sells by the sf of floor, so overage shows up as cost and freight, not as price.</p>
      </div>
    </section>
  );
}
